import RxQuery from 'utils/api/rxQuery/rxQuery';
import endpoints from 'utils/api/endpoints';
import * as payloads from 'utils/api/payloads/omantelApi';
import { AccountData } from 'utils/api/payloads/omantelApi';
import { getLanguage } from 'utils/language/language';
import { SimDetailsRequest } from '../models/products/simDetails';

const omantelApi = () => new RxQuery('omantelApi');

export const getCorpUsersForNumber = (upAccountId: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getCorpUsersForNumber)
		.setPayload(payloads.getCorpUsersForNumber(upAccountId))
		.setType('corpUsers');
};

export const requestResetPasswordOtp = (login: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.requestResetPasswordOtp)
		.setPayload(payloads.requestResetPasswordOtp(login));
};

export const getCorpUserDetails = (userId: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getCorpUserDetails)
		.setPayload(payloads.getCorpUserDetails(userId))
		.setInvalidateKey('getCorpUserDetails')
		.setType('corpUserDetails');
};

export const getCorpUserDepartmentDataForNumber = (upAccountId: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getCorpUserDepartmentDataForNumber)
		.setPayload(payloads.getCorpUserDepartmentDataForNumber(upAccountId))
		.setInvalidateKey('getCorpUserDepartmentDataForNumber')
		.setType('corpUserData');
};

export const createCorpUser = (data: payloads.NewUserData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.createCorpUser)
		.setPayload(payloads.createCorpUser(data));
};

export const getCurrentUserDetailsRest = () => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getCurrentUserDetailsRest)
		.disableCache()
		.setType('userProfile');
};

export const getCorpUsersForAccounts = () => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getCorpUsersForAccounts)
		.setInvalidateKey('getCorpUsersForAccounts')
		.setType('users');
};

export const updateCorpUserType = (data: payloads.UpdateUserTypeData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.updateCorpUserType)
		.setPayload(payloads.updateCorpUserType(data));
};

export const deleteCorpUsers = (userIds: string[]) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.deleteCorpUsers)
		.setPayload(payloads.deleteCorpUsers(userIds));
};

export const updateCorpUserPermissions = (data: payloads.UpdateUserPermissionsData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.updateCorpUserPermissions)
		.setPayload(payloads.updateCorpUserPermissions(data));
};

export const deleteAccountPermissionsForCorpUsers = (data: payloads.DeleteAccountPermissionsData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.deleteAccountPermissionsForCorpUsers)
		.setPayload(payloads.deleteAccountPermissionsForCorpUsers(data));
};

export const importUsersFromFile = (base64data: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.importUsersFromFile)
		.setPayload(payloads.importUsersFromFile(base64data))
		.setType('importedUsersData');
};

export const addCorpUserPermissions = (userId: string, permissions: payloads.PermissionData[]) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.addCorpUserPermissions)
		.setPayload(payloads.addCorpUserPermissions(userId, permissions));
};

export const sendRegisterBusinessAccount = (data: payloads.RegisterBusinessAccountData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.sendRegisterBusinessAccount)
		.setPayload(payloads.sendRegisterBusinessAccount(data));
};

export const resendActivationEmailRestPost = (userId: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.resendActivationEmailRestPost)
		.setPayload({ userId });
};

export const getNumbersForUser = (userId: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getNumbersForUser)
		.setPayload(payloads.getNumbersForUser(userId))
		.setInvalidateKey('getNumbersForUser')
		.setType('numbersForUser');
};

export const getCreditLimit = (accountInfo: payloads.AccountInfo) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getProducts)
		.setInvalidateKey('getCreditLimit')
		.setPayload(payloads.getCreditLimit(accountInfo))
		.setType('creditLimit');
};

export const getNumberUsage = (accountInfo: payloads.AccountInfo) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getNumberUsage)
		.setPayload(payloads.getNumberUsage(accountInfo))
		.setType('usageHistory');
};

export const initializeResetPassword = (userInfo: payloads.UserInfo) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.initializeResetPassword)
		.setType('resetPassword')
		.setPayload(payloads.initializeResetPassword(userInfo));
};

export const getCustomerProducts = (accountInfo: payloads.AccountInfo) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getCustomerProducts)
		.setPayload(payloads.getCustomerProducts(accountInfo))
		.setInvalidateKey('getCustomerProducts')
		.setType('customerProducts');
};

export const getProducts = (accountInfo: payloads.AccountInfo) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getProducts)
		.setPayload(payloads.getProducts(accountInfo))
		.setType('products');
};

export const createProductOrder = (productOrderInfo: payloads.ProductOrderInfo) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.createProductOrderWithOTP)
		.setHeaders('X-Omt-Channel', 'EBU', 'allowDataReportingHeader')
		.setPayload(payloads.createProductOrder(productOrderInfo));
};

export const getUsage = (accountInfo: payloads.AccountInfo) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getUsage)
		.setPayload(payloads.getUsage(accountInfo))
		.setInvalidateKey('getUsage')
		.disableCache()
		.setType('usage');
};

export const getCustomerAccountBills = () => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getCustomerAccountBills)
		.setPayload(payloads.getCustomerAccountBills())
		.setType('bills');
};

export const getCorpUserNumbersWithChilds = () => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getCorpUserNumbersWithChilds)
		.setInvalidateKey('getCorpUserNumbersWithChilds')
		.setType('accounts');
};

export const getNumbersForBillingAccount = (accountId: string, billingId: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getNumbersForBillingAccount)
		.setPayload(payloads.getNumbersForBillingAccount(accountId, billingId))
		.setInvalidateKey('getNumbersForBillingAccount')
		.setType('numbersForBillingAccount');
};

export const detachNumberFromProfile = (masterPhoneNumber: string, phoneNumber: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.detachNumberFromProfile)
		.setPayload(payloads.detachNumberFromProfile(masterPhoneNumber, phoneNumber))
		.setInvalidateKey('detachNumberFromProfile');
};

export const updateAccountNameForLoggedUser = (accountData: AccountData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.updateAccountNameForLoggedUser)
		.setPayload(payloads.updateAccountNameForLoggedUser(accountData));
};

export const getCustomerAccountOperation = (accountInfo: payloads.AccountInfo) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getCustomerAccountOperation)
		.setPayload(payloads.getCustomerAccountOperation(accountInfo))
		.setInvalidateKey('getCustomerAccountOperation')
		.setType('customerAccountResponse');
};

export const getDepartmentsForCurrentUser = () => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getDepartmentsForCurrentUser)
		.setInvalidateKey('getDepartmentsForCurrentUser')
		.setType('departments');
};

export const getCorpAccountsForCurrentUser = () => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getCorpAccountsForCurrentUser)
		.setInvalidateKey('getCorpAccountsForCurrentUser')
		.setType('corpAccounts');
};

export const updateCorpAddress = (accountDetails: payloads.AccountDetails) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.updateCorpAddress)
		.setPayload(payloads.updateCorpAddress(accountDetails));
};

export const assignNumbersForCorpUserRestPost = (userNumbers: payloads.UserNumbers) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.assignNumbersForCorpUserRestPost)
		.setPayload(payloads.assignNumbersForCorpUserRestPost(userNumbers));
};

export const changeCreditLimit = (creditLimitInfo: payloads.CreditLimitInfo) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.createProductOrderWithOTP)
		.setPayload(payloads.changeCreditLimit(creditLimitInfo));
};

export const addDepartment = (departmentData: payloads.DepartmentData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.addDepartment)
		.setPayload(payloads.addDepartment(departmentData));
};

export const getDepartmentDetails = (departmentId: string, accountId: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getDepartmentDetails)
		.setPayload(payloads.getDepartmentDetails(departmentId, accountId))
		.setInvalidateKey('getDepartmentDetails')
		.setType('departmentDetails');
};

export const getCorpAccountDetails = (accountId: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getCorpAccountDetails)
		.setPayload(payloads.getCorpAccountDetails(accountId))
		.setInvalidateKey('getCorpAccountDetails')
		.setType('accountDetails');
};

export const getCustomerBills = (customerBill: payloads.CustomerBillInfo) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getCustomerBills)
		.setPayload(payloads.getCustomerBills(customerBill))
		.setType('billPdf');
};

export const updateDepartment = (departmentData: payloads.DepartmentData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.updateDepartment)
		.setPayload(payloads.updateDepartment(departmentData));
};

export const deleteDepartment = (departmentId: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.deleteDepartment)
		.setPayload(payloads.deleteDepartment(departmentId));
};

export const getCorpAccountPhoto = (accountId: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getCorpAccountPhoto)
		.setResponseType('text')
		.setInvalidateKey('getCorpAccountPhoto')
		.setPayload(payloads.getCorpAccountPhoto(accountId));
};

export const setCorpAccountPhoto = (photoData: payloads.PhotoData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.setCorpAccountPhoto)
		.setPayload(payloads.setCorpAccountPhoto(photoData));
};

export const getNumbersForAccount = (accountId: string, unassignedOnly?: boolean) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getNumbersForAccount)
		.setPayload(payloads.getNumbersForAccount(accountId, unassignedOnly))
		.setInvalidateKey('getNumbersForAccount')
		.setType('numbersForAccount');
};

export const assignNumbersToDepartment = (departmentNumbers: payloads.DepartmentNumbers) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.assignNumbersToDepartment)
		.setPayload(payloads.assignNumbersToDepartment(departmentNumbers));
};

export const getCorpAccountAdmins = (accountId: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getCorpAccountAdmins)
		.setPayload(payloads.getCorpAccountAdmins(accountId))
		.setInvalidateKey('getCorpAccountAdmins')
		.setType('admins');
};

export const getCorpAccountManagers = (accountId: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getCorpAccountManagers)
		.setPayload(payloads.getCorpAccountManagers(accountId))
		.setInvalidateKey('getCorpAccountManagers')
		.setType('managers');
};

export const updateCorpAccountContact = (accountContactData: payloads.AccountContactData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.updateCorpAccountContact)
		.setPayload(payloads.updateCorpAccountContact(accountContactData));
};

export const getManagersForAccounts = (departmentId: string, accountId: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getManagersForAccounts)
		.setPayload(payloads.getManagersForAccounts(departmentId, accountId))
		.setType('availableAccountManagers');
};

export const assignManagersToDepartment = (data: payloads.AssignManagersToDepartmentData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.assignManagersToDepartment)
		.setPayload(payloads.assignManagersToDepartment(data));
};

export const deleteManagersFromDepartment = (data: payloads.DeleteManagersFromDepartmentData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.deleteManagersFromDepartment)
		.setPayload(payloads.deleteManagersFromDepartment(data));
};

export const makePayment = () => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.makePayment)
		.disableCache();
};

export const getCorpBillingIds = () => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getCorpBillingIds)
		.setInvalidateKey('getCorpBillingIds')
		.setType('billingIds');
};

export const getResources = () => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getResources)
		.setPayload(payloads.getResources())
		.setType('resources');
};

export const createProductOrderForPostpaidLine = (data: payloads.NewPostpaidLineData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.createProductOrderWithOTP)
		.setPayload(payloads.createProductOrderForPostpaidLine(data));
};

export const checkResourceAvailable = (data: payloads.CheckResourceAvailable) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.checkResourceAvailable)
		.setPayload(payloads.checkResourceAvailable(data))
		.setInvalidateKey('checkResourceAvailable')
		.setType('checkResourceAvailableResponse');
};

export const updateCorpUserInformation = (data: payloads.UpdateUserData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.updateCorpUserInformation)
		.setHeaders('X-Omt-Channel', 'EBU', 'allowDataReportingHeader')
		.setPayload(payloads.updateCorpUserInformation(data));
};

export const getBillsHistoryForAccounts = (data: payloads.BillsHistoryData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getBillsHistoryForAccounts)
		.setPayload(payloads.getBillsHistoryForAccounts(data))
		.setInvalidateKey('getBillsHistoryForAccounts')
		.setType('billsHistoryForAccount');
};

export const getBillingAccountInformation = ({ accountId, billingId }: { accountId: string; billingId: string }) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getBillingAccountInformation)
		.setInvalidateKey('getBillingAccountInformation')
		.setPayload({ billingId, accountId })
		.setType('billingAccountInformation');
};

export const updateBillingAccountInformation = (data: payloads.UpdateBillingAccountInformationData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.updateBillingAccountInformation)
		.setPayload(payloads.updateBillingAccountInformation(data))
		.disableCache();
};

export const getStatisticsForAccounts = () => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getStatisticsForAccounts)
		.setType('statisticsForAccounts');
};

export const getPaymentsHistoryForAccounts = (paymentsData: payloads.PaymentsHistoryData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getPaymentsHistoryForAccounts)
		.setPayload(payloads.getPaymentsHistoryForAccounts(paymentsData));
	//.setType('payments');
};

export const deleteNumbersAndUsersFromDepartment = (data: payloads.NumbersUsersToDeleteData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.deleteNumbersAndUsersFromDepartment)
		.setPayload(payloads.deleteNumbersAndUsersFromDepartment(data));
};

export const getPaymentHistoryRowItems = (paymentRequestId: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getPaymentHistoryRowItems)
		.setPayload(payloads.getPaymentHistoryRowItems(paymentRequestId))
		.setType('paymentDetails');
};

export const getEBillInformation = (data: payloads.EBillData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getProducts)
		.setPayload(payloads.getEBillInformation(data));
};

export const validateResetPasswordEmail = (email: string, token: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.validateResetPasswordEmail)
		.setPayload(payloads.validateResetPasswordEmail(email, token));
};

export const resetPassword = (data: payloads.ResetPasswordData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.resetPassword)
		.setPayload(payloads.resetPassword(data));
};

export const changePassword = (data: payloads.ChangePasswordData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.changePassword)
		.setPayload(payloads.changePassword(data));
};

export const getPaymentReceipt = (paymentId: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getPaymentReceipt)
		.setPayload(payloads.getPaymentReceipt(paymentId));
};

export const updateEbillEmails = (data: payloads.UpdateEbillsEmailsData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.updateEbillEmails)
		.setPayload(payloads.updateEbillEmails(data));
};

export const getCorpAccountsWithAdminsForCurrentUser = () => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getCorpAccountsWithAdminsForCurrentUser)
		.setPayload(payloads.getCorpAccountsWithAdminsForCurrentUser())
		.setInvalidateKey('getCorpAccountsWithAdminsForCurrentUser')
		.setType('corpAccounts');
};

export const updateServices = (data: payloads.ServiceData) => {
	return omantelApi()
		.setEndpoint(
			data?.otp?.length > 0
				? endpoints.omantelApi.createProductOrderWithOTP
				: endpoints.omantelApi.createProductOrder
		)
		.setPayload(payloads.updateServices(data))
		.setType('createProductOrderResponse');
};

export const submitServices = (data: payloads.SubmitServicesData) => {
	const language = getLanguage();
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.submitProductOrderOperation)
		.setPayload(payloads.submitServices({ ...data, language }))
		.setType('productOrderResponse');
};
export const deleteAllAccountsForArborIdOperation = (
	accountId: string,
	orgAccountId: string,
	token?: string,
	documentId?: string
) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.deleteAllAccountsForArborIdOperation)
		.setPayload(payloads.deleteAllAccountsForArborIdOperation(accountId, orgAccountId, token, documentId));
};

export const deleteAllAccountsForArborIdInitialize = (accountId: string, orgAccountId: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.deleteAllAccountsForArborIdInitialize)
		.setPayload(payloads.deleteAllAccountsForArborIdOperation(accountId, orgAccountId));
};

export const addAllAccountsForArborIdInitialize = (data: payloads.AddAllAccountsForArborIdInitializeData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.addAllAccountsForArborIdInitialize)
		.setPayload(payloads.addAllAccountsForArborIdInitialize(data));
};

export const addAllAccountsForArborIdOperation = (data: payloads.AddBillingAccountData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.addAllAccountsForArborIdOperation)
		.setPayload(payloads.addAllAccountsForArborIdOperation(data));
};

export const searchNumbersAndUsersRestPost = (searchText: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.searchNumbersAndUsersRestPost)
		.setPayload({ searchText })
		.setType('searchResults');
};

export const getAccountNumberInfo = (accountNumber: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getAccountNumberInfo)
		.setPayload(payloads.getAccountNumberInfo(accountNumber))
		.setType('getAccountNumberInfoByNumber');
};

export const detachNumbersFromUser = (corpUserId: string, numbers: string[]) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.detachNumbersFromUser)
		.setPayload(payloads.detachNumbersFromUser(corpUserId, numbers));
};

export const contactUsSelfcare = (data: payloads.ContactUsSelfcareData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.contactUsSelfcare)
		.setPayload(payloads.contactUsSelfcare(data));
};

export const sendDirectMessage = (data: payloads.DirectMessageData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.directContactOperation)
		.setPayload(data);
};

export const retrieveOsmIncident = (data: payloads.RetrieveOsmIncidentData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.retrieveOsmIncident)
		.setPayload(payloads.retrieveOsmIncident(data))
		.setType('incident');
};

export const retrieveOsmInteraction = (data: payloads.RetrieveOsmInteractionData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.retrieveOsmInteraction)
		.setPayload(payloads.retrieveOsmInteraction(data))
		.setType('interaction');
};

export const getCustomerUnpaidBillsForAccounts = (billingId?: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getCustomerUnpaidBillsForAccounts)
		.setPayload(payloads.getCustomerUnpaidBillsForAccounts(billingId))
		.setInvalidateKey('getCustomerUnpaidBillsForAccounts')
		.setHeaders('X-Omt-Channel', 'EBU', 'allowDataReportingHeader')
		.disableCache()
		.setType('unpaidBills');
};

export const getCpnAccount = (billingAccountNumber: string, phoneNumber: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getCpnAccount)
		.setPayload(payloads.getCpnAccount(billingAccountNumber, phoneNumber))
		.setInvalidateKey('getCpnAccount')
		.setType('cpnAccount');
};

export const getBillAccountNoForNumber = (number: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getBillAccountNoForNumber)
		.setPayload({ number })
		.setType('billingAccountNoForNumber');
};

export const activateStaticIp = (phoneNumber: string, productId: string, staticIpId: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.submitProductOrderOperation)
		.setPayload(payloads.activateStaticIp(phoneNumber, productId, staticIpId))
		.setType('productOrderResponse');
};

export const getStaticIPOrderStatus = (phoneNumber: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getOrderStatus)
		.setPayload(payloads.getStaticIPOrderStatus(phoneNumber));
};

export const activateCpn = (phoneNumber: string, billingId: string, productId: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.submitProductOrderOperation)
		.setPayload(payloads.activateCpn(phoneNumber, billingId, productId))
		.setType('productOrderResponse');
};

export const deactivateCpn = (phoneNumber: string, billingId: string, productId: string, shortCode?: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.submitProductOrderOperation)
		.setPayload(payloads.deactivateCpn(phoneNumber, billingId, productId, shortCode))
		.setType('productOrderResponse');
};

export const addShortCode = (phoneNumber: string, shortCode: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.submitProductOrderOperation)
		.setPayload(payloads.addShortCode(phoneNumber, shortCode))
		.setType('productOrderResponse');
};

export const updateOrganizationTaxId = (data: payloads.UpdateOrganizationTaxId) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.updateOrganizationTaxId)
		.setPayload(payloads.updateOrganizationTaxId(data))
		.setType('updateOrganizationTaxId');
};

export const removeShortCode = (phoneNumber: string, shortCode: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.submitProductOrderOperation)
		.setPayload(payloads.removeShortCode(phoneNumber, shortCode))
		.setType('productOrderResponse');
};

export const changeShortCode = (phoneNumber: string, newShortCode: string, oldShortCode: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.submitProductOrderOperation)
		.setPayload(payloads.changeShortCode(phoneNumber, newShortCode, oldShortCode))
		.setType('productOrderResponse');
};

export const getPaymentTokensForCurrentUser = () => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getPaymentTokensForCurrentUser)
		.setInvalidateKey('getPaymentTokensForCurrentUser')
		.setType('paymentTokens');
};

export const removePaymentToken = (paymentTokenId: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.removePaymentToken)
		.setPayload({ paymentTokenId })
		.disableCache();
};

export const createHbbAccount = (data: payloads.CreateHbbAccountData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.createHbbAccount)
		.setPayload(payloads.createHbbAccount(data))
		.disableCache();
};

export const validateAdslLogin = (adslLogin: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.validateAdslLogin)
		.setPayload(payloads.validateAdslLogin(adslLogin))
		.disableCache();
};

export const changeAdslPassword = (oldPassword: string, newPassword: string, adslLogin: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.createProductOrderWithOTP)
		.setPayload(payloads.changeAdslPassword(oldPassword, newPassword, adslLogin))
		.setType('createProductOrderResponse');
};

export const sendResetAdslPasswordOtp = (adslLogin: string, msisdn: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.sendResetAdslPasswordOtp)
		.setPayload({ adslLogin, msisdn });
};

export const confirmAdslResetPasswordOtp = (newPassword: string, token: string, adslLogin: string, msisdn: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.confirmAdslResetPasswordOtp)
		.setPayload({ newPassword, token, adslLogin, msisdn });
};

export const getFbbEmails = (adslLogin: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getProducts)
		.setPayload(payloads.getFbbEmails(adslLogin))
		.setType('getFbbEmailsResponse');
};

export const addFbbEmail = (emailLogin: string, password: string, adslLogin: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.createProductOrderWithOTP)
		.setPayload(payloads.addFbbEmail(emailLogin, password, adslLogin))
		.setType('createProductOrderResponse');
};

export const modifyFbbEmailPassword = (password: string, email: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.createProductOrderWithOTP)
		.setPayload(payloads.modifyFbbEmailPassword(password, email))
		.setType('createProductOrderResponse');
};

export const getAccountManagerDetails = (documentId: string, documentType: types.organizations.documentType) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getAccountManagerDetails)
		.setPayload({ documentId, documentType })
		.setType('accountManagers');
};

export const getSimDetails = (data: SimDetailsRequest) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getSimDetails)
		.setPayload(data)
		.setInvalidateKey('getSimDetails')
		.setType('simDetails')
		.disableCache();
};

export const getCommitmentDetails = (subscriberNo: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getCommitmentDetails)
		.setPayload({ subscriberNo })
		.setInvalidateKey('getCommitmentDetails')
		.setType('commitmentDetailsResponse');
};

export interface IChangeFixedPlan {
	partNumber: string;
	phoneNumber: string;
	accountNumber: string;
	accountExternalId: string;
	subscriberNo: string;
	oldPartNumber: string;
}

export const changeFixedPlan = ({
	partNumber,
	phoneNumber,
	accountNumber,
	accountExternalId,
	subscriberNo,
	oldPartNumber,
}: IChangeFixedPlan) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.createProductOrderWithOTP)
		.disableCache()
		.setPayload(
			payloads.changeFixedPlan({
				partNumber,
				phoneNumber,
				accountNumber,
				accountExternalId,
				subscriberNo,
				oldPartNumber,
			})
		)
		.setType('createProductOrderResponse');
};

export const requestForOtp = (operationContext: payloads.OperationContextEnum, msisdn: string) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.requestForOtp)
		.disableCache()
		.setPayload({ operationContext, msisdn });
};

export const deleteCorpUserPermissions = (data: payloads.CorpUserPermissionsData) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.deleteCorpUserPermissions)
		.disableCache()
		.setPayload(data);
};

export const getOrders = (data: any) => {
	return omantelApi()
		.setEndpoint(endpoints.omantelApi.getOrders)
		.setPayload(data);
};
